import { fetchTerrainHeights, generateTerrainSamples } from 'app/shared/path_profile/path_profile';
import { useContext, useEffect, useState } from 'react';
import { Cartesian3, Cartographic } from 'cesium';
import { ServicesContext } from 'app/items/shared/context_interface';
import { StatDisplay } from 'app/agriculture/components/StatDisplay/StatDisplay';
import { areShapesEqual } from 'app/agriculture/utils';
import { Position } from 'app/agriculture/queries/types/geometry/Position';
import { TerrainProviderContext } from 'app/agriculture/contexts/TerrainContextProvider';

interface PolylineLengthComponentProps {
    id: string;
    line: Position[];
    noun: string;
}

export function PolylineLengthComponent({ id, line, noun }: PolylineLengthComponentProps) {
    const { terrainService } = useContext(ServicesContext);
    const provider = useContext(TerrainProviderContext);

    const [lineLength, setLineLength] = useState<string>('0');
    const [storedLine, setStoredLine] = useState<Position[]>();
    const [isCalculating, setIsCalculating] = useState(false);

    useEffect(() => {
        if (!areShapesEqual(storedLine, line)) {
            setStoredLine(line);
        }
    }, [line]);

    useEffect(() => {
        let active = true;
        setIsCalculating(true);
        getLength(storedLine)
            .then((distance) => {
                if (!active) {
                    // bail, our useEffect was recreated or the component removed during our promise execution, don't touch anything on our way out
                    return;
                }
                setIsCalculating(false);
                setLineLength(distance);
            })
            .catch((e: unknown) => {
                console.error(e);
            });

        return () => {
            active = false;
        };
    }, [storedLine]);

    async function getLength(line?: Position[]) {
        if (provider == null) {
            throw new Error('CesiumTerrainProvider is null');
        }

        const points = line?.map((i) => Cartographic.fromDegrees(i[0], i[1]));
        const allPoints: Cartographic[] = [];

        if (points) {
            for (let i = 0; i < points.length - 1; i++) {
                const { terrainSamples } = generateTerrainSamples(points[i], points[i + 1]);
                allPoints.push(...terrainSamples);
            }
            const pointsWithHeight = await fetchTerrainHeights(allPoints, provider, terrainService);

            let distance = 0;
            for (let i = 0; i < pointsWithHeight.length - 1; i++) {
                const point1 = Cartographic.toCartesian(pointsWithHeight[i]);
                const point2 = Cartographic.toCartesian(pointsWithHeight[i + 1]);
                distance += Cartesian3.distance(point1, point2);
            }

            return distance.toFixed(2);
        }
        return '0';
    }

    return (
        <StatDisplay
            id={id}
            value={lineLength}
            name={noun}
            unit={'M'}
            isCalculating={isCalculating}
        />
    );
}
